import { useCallback, useEffect, useRef } from 'react';
import { useWebSocket } from './useWebSocket';

interface IrisStatus {
  state: 'open' | 'closed' | 'unknown';
  action?: 'open' | 'close';
  success?: boolean;
  error?: string;
  timestamp: number;
}

interface IrisControlHook {
  openIris: (duration?: number) => void;
  closeIris: () => void;
  getIrisStatus: () => void;
  irisState: 'open' | 'closed' | 'unknown';
  isConnected: boolean;
  isBusy: boolean;
}

// Global state to track iris busy status across all hook instances
let globalIrisBusy = false;
let globalIrisBusyUntil = 0;

export function useIrisControl(): IrisControlHook {
  const { sendMessage, isConnected } = useWebSocket();
  const irisStateRef = useRef<'open' | 'closed' | 'unknown'>('unknown');
  const isBusyRef = useRef<boolean>(false);

  // Handle iris status messages
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      try {
        const message = JSON.parse(event.data);
        
        if (message.type === 'iris_status') {
          const status = message as IrisStatus;
          
          // Update iris state
          if (status.state) {
            irisStateRef.current = status.state;
          }
          
          // Log status for debugging
          console.log('🔵 Iris status:', {
            state: status.state,
            action: status.action,
            success: status.success,
            error: status.error
          });
          
          // Handle errors
          if (status.error) {
            console.error('❌ Iris control error:', status.error);
          }
        }
      } catch (error) {
        console.error('Error parsing iris message:', error);
      }
    };

    // Get WebSocket instance from window
    const ws = (window as any).webSocketInstance;
    if (ws) {
      ws.addEventListener('message', handleMessage);
      
      // Query initial status when connected
      if (isConnected) {
        sendMessage({ type: 'iris_status' });
      }
      
      return () => {
        ws.removeEventListener('message', handleMessage);
      };
    }
  }, [isConnected, sendMessage]);

  // Check if iris is currently busy (within cooldown period)
  const checkIsBusy = useCallback(() => {
    const now = Date.now();
    if (now < globalIrisBusyUntil) {
      return true;
    }
    globalIrisBusy = false;
    return false;
  }, []);

  // Open iris with optional duration - with busy protection
  const openIris = useCallback((duration?: number) => {
    if (!isConnected) {
      console.warn('⚠️ Cannot open iris: WebSocket not connected');
      return;
    }

    // Check if iris is busy (still in open/close cycle)
    if (checkIsBusy()) {
      const remainingTime = Math.ceil((globalIrisBusyUntil - Date.now()) / 1000);
      console.warn(`⚠️ Iris is busy - please wait ${remainingTime}s before sending another command`);
      return;
    }

    // Set busy state with cooldown (duration + 1 second buffer for mechanical movement)
    const cooldownMs = ((duration || 5) + 1) * 1000;
    globalIrisBusy = true;
    globalIrisBusyUntil = Date.now() + cooldownMs;
    isBusyRef.current = true;

    console.log(`🔵 Opening iris${duration ? ` for ${duration} seconds` : ''}... (cooldown: ${cooldownMs}ms)`);

    sendMessage({
      type: 'iris_open',
      duration: duration
    });

    // Clear busy state after cooldown
    setTimeout(() => {
      globalIrisBusy = false;
      isBusyRef.current = false;
      console.log('🔵 Iris cooldown complete - ready for next command');
    }, cooldownMs);
  }, [isConnected, sendMessage, checkIsBusy]);

  // Close iris
  const closeIris = useCallback(() => {
    if (!isConnected) {
      console.warn('⚠️ Cannot close iris: WebSocket not connected');
      return;
    }

    console.log('🔵 Closing iris...');

    sendMessage({
      type: 'iris_close'
    });

    // Clear busy state when explicitly closing
    globalIrisBusy = false;
    globalIrisBusyUntil = 0;
    isBusyRef.current = false;
  }, [isConnected, sendMessage]);

  // Get current iris status
  const getIrisStatus = useCallback(() => {
    if (!isConnected) {
      console.warn('⚠️ Cannot get iris status: WebSocket not connected');
      return;
    }

    sendMessage({
      type: 'iris_status'
    });
  }, [isConnected, sendMessage]);

  return {
    openIris,
    closeIris,
    getIrisStatus,
    irisState: irisStateRef.current,
    isConnected,
    isBusy: checkIsBusy()
  };
}

// Export convenience functions for direct use
export const irisControl = {
  open: (duration?: number) => {
    const ws = (window as any).webSocketInstance;

    // Check if iris is busy
    const now = Date.now();
    if (now < globalIrisBusyUntil) {
      const remainingTime = Math.ceil((globalIrisBusyUntil - now) / 1000);
      console.warn(`⚠️ Iris is busy - please wait ${remainingTime}s before sending another command`);
      return;
    }

    if (ws && ws.readyState === WebSocket.OPEN) {
      // Set busy state with cooldown
      const cooldownMs = ((duration || 5) + 1) * 1000;
      globalIrisBusy = true;
      globalIrisBusyUntil = Date.now() + cooldownMs;

      ws.send(JSON.stringify({
        type: 'iris_open',
        duration: duration
      }));

      // Clear busy state after cooldown
      setTimeout(() => {
        globalIrisBusy = false;
      }, cooldownMs);
    } else {
      console.warn('⚠️ WebSocket not connected');
    }
  },

  close: () => {
    const ws = (window as any).webSocketInstance;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({
        type: 'iris_close'
      }));
      // Clear busy state when explicitly closing
      globalIrisBusy = false;
      globalIrisBusyUntil = 0;
    } else {
      console.warn('⚠️ WebSocket not connected');
    }
  },

  isBusy: () => {
    return Date.now() < globalIrisBusyUntil;
  },

  status: () => {
    const ws = (window as any).webSocketInstance;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({
        type: 'iris_status'
      }));
    } else {
      console.warn('⚠️ WebSocket not connected');
    }
  }
};
